import { defineComponent as _defineComponent } from 'vue'
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { TabType } from '@/interfaces/inner-tabs';

import WatchNowModal from '@/components/modals/WatchNowModal.vue';
import { ModalHelper } from '@/helpers/modal-helper';
import { ModalHelper as ModalHelperVFM } from '@/helpers/modal-helper-vfm';
import { WatchlistTitleGraphqlFragment } from '@/components/watchlist/graphql/fragments/WatchlistTitle.fragment';
import { FilterCollection, getDefaultFilterCollection } from '@/stores/filter.store';

import { isFilterActive } from '@/helpers/filter-helper';
import { TvShowTrackingTitleFragment } from '@/components/watchlist/graphql/fragments/TVShowTrackingTitle.fragment';
import { ClickoutUrl, getClickoutUrlContextsArray, handleClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import {
	useLanguageStore,
	useFilterStore,
	useUserStore,
	useRoutingStore,
	useAppStore,
} from '@/helpers/composables/useStores';

import { computed, inject } from 'vue';
import { getClickoutTrackingPlacement, OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';
import { ObjectType } from '@/@types/graphql-types';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';


export default /*#__PURE__*/_defineComponent({
  __name: 'WatchNowButtonNew',
  props: {
    title: null,
    customFilters: { default: getDefaultFilterCollection },
    size: { default: 'default' },
    rounded: { type: Boolean, default: true },
    color: { default: 'secondary' },
    uniqueProviders: { type: Boolean },
    inQuickActions: { type: Boolean },
    isNextEpisodeButton: { type: Boolean },
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { title: WatchlistTitleGraphqlFragment['node'] | TvShowTrackingTitleFragment['node'], customFilters: FilterCollection, size: 'default' | 'large' | 'slim' | 'none', rounded: boolean, color: 'secondary' | 'primary' | 'dark', uniqueProviders?: boolean, inQuickActions?: boolean, isNextEpisodeButton?: boolean, additionalContexts: SnowplowContext[] };



const { country } = useLanguageStore();
const { xServeTrack } = useAppStore();
const { clickoutType, placement } = getClickoutTrackingPlacement('watch_now');

const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const watchNextEpisode = computed(() =>
	'watchNextEpisode' in props.title && props.isNextEpisodeButton ? props.title.watchNextEpisode : null
);

const watchNowOffer = computed(() => watchNextEpisode.value?.watchNowOffer ?? props.title?.watchNowOffer);
const isWatchnowOfferAvailable = computed(() => !!watchNowOffer.value);

const titleObjectId = computed(() =>
	props.title.__typename === 'Season' && props.title.show ? props.title.show.objectId : props.title.objectId
);
const titleObjectType = computed(() =>
	props.title.__typename === 'Season' ? ObjectType.Show : props.title.objectType
);

const titleContext = computed(() => {
	const title = watchNextEpisode.value ?? props.title;
	return new SnowplowTitleContextGraphql(
		titleObjectId.value,
		titleObjectType.value,
		title.content.__typename === 'SeasonContent' || title.content.__typename === 'EpisodeContent'
			? title.content?.seasonNumber
			: undefined,
		title.content.__typename === 'EpisodeContent' ? title.content?.episodeNumber : undefined
	);
});

const { onVisibilityChanged } = useOfferImpressionTracking({
	additionalContexts: () => [...props.additionalContexts, titleContext.value],
});

// collectionType will not change anymore
// we don't rely on activeCollectionType or currentFilters because it would recalculate when the store changes
const { activeCollectionType } = useRoutingStore();
const collectionType = activeCollectionType.value;
const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);

const offerCount = computed(() => {
	if (props.uniqueProviders) {
		// @ts-ignore
		return [...new Set(props.title.offers.map(offer => offer.package?.packageId).filter(Boolean))].length;
	}

	return props.isNextEpisodeButton && 'watchNextEpisode' in props.title
		? props.title.watchNextEpisode?.offerCount || 0
		: props.title.offerCount;
});

const { filters } = useFilterStore();
const currentFilters = computed(() => ({
	...(filters.value(collectionType) ?? getDefaultFilterCollection()),
	...props.customFilters,
}));

const { t, tc } = useI18n();
const buttonText = computed(() => {
	if (offerCount.value === 0) {
		return t('WEBAPP_CURRENTLY_NO_OFFERS');
	}

	const isSeenTab = isFilterActive('inner_tab', TabType.SEENLIST, currentFilters.value);
	const message = isSeenTab ? tc('WEBAPP_WATCH_AGAIN') : tc('WEBAPP_SEENLIST_WATCHNOW');

	const offersTranslation =
		offerCount.value > 1
			? t('WEBAPP_X_OFFERS_AVAILABLE', { offerCount: offerCount.value })
			: t('WEBAPP_1_OFFER_AVAILABLE', { offerCount: offerCount.value });

	return isWatchnowOfferAvailable.value ? message : offersTranslation;
});

const providerShortName = computed(() => watchNowOffer.value?.package.shortName ?? null);

const clickoutUrl = computed(() => {
	const url = new ClickoutUrl(watchNowOffer.value)
		.setTitleSourceId(props.title.id)
		.addContexts(props.additionalContexts)
		.addContexts(titleContext.value)
		.set('uct_country', country.value)
		.set('uct_tryout', 1)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.setCanary(xServeTrack.value);

	// Amazon streaming bounty redirects
	if ([9, 119].includes(watchNowOffer.value?.package.packageId ?? 0)) {
		url.set('r', url.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}
	return url;
});

const { setBuyboxPresentationType } = useUserStore();
const { openRentalModal } = useRentalModal();
function handleWatchNow() {
	const contexts = [...props.additionalContexts, titleContext.value];

	if (!isWatchnowOfferAvailable.value) {
		ModalHelperVFM.openModal(WatchNowModal, {
			title: props.title,
			isNextEpisodeButton: props.isNextEpisodeButton,
		}).then(() => setBuyboxPresentationType({ presentationType: null }));
	}

	const offer = watchNowOffer.value;
	if (!offer) return;

	if (offer?.standardWebURL) {
		// JUSWATCH_TV
		if (offer.package.technicalName === 'justwatchtv') {
			const isRentOffer = isJWTVRentalOffer(offer);
			trackClickout(clickoutUrl.value);
			if (isRentOffer) {
				openRentalModal({
					offer: offer,
					title: props.title,
					clickoutUrl: clickoutUrl.value,
					overrideRedirectUrl: true,
				});
			} else {
				ModalHelper.openModal(
					() => import('@/ui-components/video-player-modals/JwTVModal.vue'),
					{
						offer: offer,
						title: props.title,
						additionalContexts: getClickoutUrlContextsArray(clickoutUrl.value),
					},
					{ cssClass: 'trailer-modal' },
					'',
					true,
					[]
				);
			}
		} else {
			handleClickoutUrl(clickoutUrl.value, false);
		}
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'watchnow_clicked',
			property: props.inQuickActions + '',
		},
		contexts.concat(SnowplowClickoutContextGraphql.fromProviderOffer(offer))
	);
}

return { __sfc: true,props, country, xServeTrack, clickoutType, placement, episodeEntityId, watchNextEpisode, watchNowOffer, isWatchnowOfferAvailable, titleObjectId, titleObjectType, titleContext, onVisibilityChanged, activeCollectionType, collectionType, jwRawEntityId, offerCount, filters, currentFilters, t, tc, buttonText, providerShortName, clickoutUrl, setBuyboxPresentationType, openRentalModal, handleWatchNow, ProviderIcon, BasicButton }
}

})